import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getAllPlannedWorkouts, getPlanOverview } from "@/api/plan";
import { Card, SectionLabel } from "@/components/ui/Card";
import { WeekCard } from "@/components/plan/WeekCard";
import { CalendarView, type CalendarWorkout } from "@/components/plan/CalendarView";
import { fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

type Filter = "all" | "upcoming" | "checkpoint";

export default function PlanOverview() {
  const { t } = useTranslation();
  const FILTERS = [
    { id: "all" as const, label: t("plan.filter_all") },
    { id: "upcoming" as const, label: t("plan.filter_upcoming") },
    { id: "checkpoint" as const, label: t("plan.filter_checkpoint") },
  ];
  const [filter, setFilter] = useState<Filter>("all");
  const [mode, setMode] = useState<"list" | "calendar">("list");

  const planQ = useQuery({ queryKey: ["plan"], queryFn: getPlanOverview });
  const workoutsQ = useQuery({
    queryKey: ["plan-workouts"],
    queryFn: getAllPlannedWorkouts,
    enabled: mode === "calendar",
  });

  const calendarWorkouts = useMemo<CalendarWorkout[]>(() => {
    if (!workoutsQ.data) return [];
    return workoutsQ.data.map((w) => ({
      id: w.id,
      date: w.date,
      sport: w.sport,
      title: w.title,
      duration_min: w.duration_minutes,
      tss: w.target_tss,
      status: w.status,
    }));
  }, [workoutsQ.data]);

  if (planQ.isLoading) {
    return <div className="py-10 text-center text-taupe">{t("plan.loading_plan")}</div>;
  }
  if (!planQ.data) {
    return <div className="py-10 text-center text-coral">{t("plan.not_found")}</div>;
  }
  const data = planQ.data;

  const today = new Date().toISOString().slice(0, 10);
  const currentWeekIdx = data.weeks.findIndex(
    (w) =>
      w.week_start_date <= today &&
      today <= addDays(w.week_start_date, 6),
  );

  const filtered = data.weeks.filter((w) => {
    if (filter === "upcoming") return w.week_start_date >= today;
    if (filter === "checkpoint") return [12, 20].includes(w.week_number);
    return true;
  });

  const phases = [...new Set(data.weeks.map((w) => w.phase))];

  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>{t("plan.title_26w")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[28px] font-medium tracking-tight">
          {t("plan.from_to", { from: fmtDate(data.start_date!, "d MMM"), to: fmtDate(data.end_date!, "d MMM yyyy") })}
        </h1>
      </div>

      <Card>
        <SectionLabel>{t("plan.phases")}</SectionLabel>
        <div className="mt-3 flex flex-wrap gap-2">
          {phases.map((p) => (
            <span
              key={p}
              className="rounded-full border border-cream-border bg-cream-light px-3 py-1 text-[12px] font-medium text-navy"
            >
              {p}
            </span>
          ))}
        </div>
      </Card>

      {/* View mode toggle */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex rounded-full border border-cream-border bg-cream-light p-0.5">
          {(["list", "calendar"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-[13px] transition",
                mode === m
                  ? "bg-navy text-cream-light"
                  : "text-taupe hover:text-navy",
              )}
            >
              {m === "list" ? t("plan.view_list") : t("plan.view_calendar")}
            </button>
          ))}
        </div>

        {mode === "list" ? (
          <div className="flex gap-1.5 overflow-x-auto">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  "whitespace-nowrap rounded-full border px-3 py-1 text-[12px] transition",
                  filter === f.id
                    ? "border-amber bg-amber/15 text-amber-dark"
                    : "border-cream-border bg-cream-light text-taupe hover:text-navy",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {mode === "list" ? (
        <div className="space-y-2.5">
          {filtered.map((w) => (
            <WeekCard
              key={w.week_number}
              week={w}
              isCurrent={w.week_number === currentWeekIdx + 1}
            />
          ))}
        </div>
      ) : workoutsQ.isLoading ? (
        <div className="py-6 text-center text-[13px] text-taupe">
          {t("plan.loading_workouts")}
        </div>
      ) : (
        <CalendarView
          weeks={data.weeks.map((w) => ({
            weekNumber: w.week_number,
            startDate: w.week_start_date,
            phase: w.phase,
            targetTss: w.target_tss,
          }))}
          workouts={calendarWorkouts}
          linkPrefix="/workout/planned/"
          emptyMessage={t("plan.empty_msg")}
        />
      )}
    </div>
  );
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
